import { useState, useEffect } from 'react'
import Button from 'components/button'
import { Input, Label, ErrorInput } from 'theme/theme'
import Select, { StylesConfig } from 'react-select'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import formServices from 'services/form-builder-services'
import { ErrorMessage } from '@hookform/error-message'
import { AxiosError } from 'axios'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { Question } from 'contexts/survey-context/survey.interface'
import ProfileService from 'services/profile-services'
import useRouter from 'hooks/use-router'
import ButtonCloseMenu from 'components/button-close'
import { TitleSurvey } from '../../../styles/styled.components'

interface IProps {
  title: string
  setInfo: any
  info: any
  changeStep: (count: string) => void
  answers: any
  question: Question
}

const StepOne = ({ title, setInfo, info, changeStep, answers, question }: IProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: formServices.formValidateErrors(
      yupResolver(formServices.formGenerateSchema(['postalCode'])),
      'address',
      'datos cuenta'
    )
  })
  const [data, setData] = useState<any[]>([])

  // I grab user from localstorage because the useSsesion is generating an "infinite loop"
  const ls = localStorage.getItem('state')
  const dataUser = ls ? JSON.parse(ls) : {}

  const styles: StylesConfig = {
    option: (provided) => {
      return {
        ...provided,
        fontFamily: 'DINPro',
        fontSize: '16px',
        fontWeight: 400
      }
    },
    container: (provided) => {
      return {
        ...provided,
        fontFamily: 'DINPro',
        fontSize: '16px',
        fontWeight: 400,
        color: !data.length ? '#747274' : 'black',
        padding: '0.4rem',
        borderRadius: '5px',
        background: !data.length ? 'transparent' : 'white',
        outline: 'none',
        '&:focus': {
          border: '1px solid #000'
        },
        border: '1px solid #000000'
      }
    },
    singleValue: (provided) => {
      return {
        ...provided,
        color: '#000',
        fontFamily: 'DINPro',
        fontSize: '16px',
        fontWeight: 400,
        textAlign: 'start'
      }
    },
    control: (provided) => {
      return {
        ...provided,
        color: '#000',
        background: 'none',
        border: 'none',
        outline: 'none',
        '&:focus': {
          border: 'none'
        },
        textAlign: 'center'
      }
    },
    input: (provided) => {
      return {
        ...provided,
        color: '#000',
        background: 'none',
        border: 'none',
        outline: 'none',
        '&:focus': {
          border: 'none'
        },
        textAlign: 'start'
      }
    },
    placeholder: (provided) => {
      return {
        ...provided,
        color: '#747274',
        background: 'none',
        border: 'none',
        outline: 'none',
        '&:focus': {
          border: 'none'
        },
        textAlign: 'start',
        fontFamily: 'DINPro',
        fontWeight: 800
      }
    },
    dropdownIndicator: (provided) => ({
      ...provided,
      color: !data.length ? '#747274' : '#008065'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  }

  async function onFormSubmit() {

    dlTrackEvent('form_send', {
      element: 'siguiente',
      form_name: 'nomina tu cp',
      section: 'survey :: nomina cp'
    })

    answers.push({
      optionIds: [question?.options[0]?.optionId],
      questionId: question?.questionId,
      textValue: `${info.neighborhoodId?.value}`
    })
    changeStep('increment')
  }

  const [debouncedPostalCode, setDebouncedPostalCode] = useState(info.postalCode)
  const [selectedColonia, setSelectedColonia] = useState(info.neighborhoodId)
  const [error, setError] = useState(null)
  const { history } = useRouter()

  const searchZipCode = async () => {
    try {

      const completeField = info?.postalCode?.length > 4;
      dlTrackEvent('form_interaction', {
        form_field: 'codigo postal',
        form_action: completeField ? 'complete' : 'empty',
        form_name: 'nomina tu cp',
        section: 'survey :: nomina cp'
      })

      if (completeField) {

        toast.loading('Buscando código postal', { position: 'top-center' })
        const response = await ProfileService.checkZip({ postalCode: info?.postalCode }, { session: dataUser?.data })
        toast.dismiss()

        if (response.status === 204) {
          toast.error('Ups! No hemos encontrado el código postal, chequea nuevamente.', { position: 'bottom-center' })
        } else {
          setError(null)
          const infoObject = { ...info, state: response.data[0].name, municipality: response.data[0].cities[0].name }
          if (info?.postalCode !== debouncedPostalCode) {
            infoObject.neighborhoodId = null
            setSelectedColonia(null)
          };

          setInfo(infoObject)
          setData(response.data[0].cities[0].neighborhoods)
          setDebouncedPostalCode(info.postalCode)
        }
      } else {
        setData([])
        setInfo({
          ...info,
          neighborhoodId: null,
          municipality: '',
          state: ''
        })
        setSelectedColonia('')
      }

    } catch (err) {
      const typedError = err as AxiosError
      const { code } = typedError.response?.data || {}
      setError(code)
      toast.dismiss()
      toast.error('Ups! No hemos encontrado el código postal, chequea nuevamente.', { position: 'bottom-center' })
      setData([])
      setInfo({
        ...info,
        state: '',
        neighborhoodId: null,
        municipality: ''
      })
      setSelectedColonia('')
    }
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      searchZipCode()
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [info?.postalCode])

  const isInfoComplete = () => {
    return info.neighborhoodId !== null
  }

  return (
    <form className="flex justify-between flex-col items-center h-full w-full" onSubmit={handleSubmit(onFormSubmit)}>
      <ButtonCloseMenu
        open={true}
        paramFn={() => {
          gtag('event', 'Click', {
            event_category: GTM_EVENT_CATEGORY,
            event_label: 'Survey - Cerrar modal',
            value: '10'
          })
          history.goBack();
        }}
      />
      <div className="w-full">
        <TitleSurvey className="font-black text-4xl text-center py-4 title-survey" style={{ fontSize: '38px' }}>
          {title}
        </TitleSurvey>

        <div className="flex flex-col mt-5 mx-auto" style={{ maxWidth: '323px' }}>
          <Label>¿Código postal?</Label>
          <div className="" style={{ maxWidth: '323px' }}>
            <Input
              type="number"
              className={`w-full ${error ? 'invalid' : ''}`}
              placeholder="Ingresa tu Código postal"
              style={{ paddingLeft: '18px', paddingRight: '18px' }}
              maxLength={8}
              value={info?.postalCode}
              {...register('postalCode')}
              onChange={(e) => {
                setInfo({
                  ...info,
                  postalCode: e.target.value,
                  colonia: ''
                })
              }}
            />
            <ErrorInput className="input-error">
              <ErrorMessage errors={errors} name="postalCode" />
            </ErrorInput>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 mx-auto" style={{ maxWidth: '323px' }}>
          <div className="flex flex-col">
            <Label>¿Tu colonia?</Label>
            <div className="" style={{ maxWidth: '323px' }}>
              <Select
                styles={styles}
                placeholder="Colonia"
                options={data?.map((item) => ({ value: item?.id, label: item?.name }))}
                isDisabled={!data.length}
                value={selectedColonia}
                onChange={(e: { value: any; label: string }) => {
                  setInfo({
                    ...info,
                    neighborhoodId: e
                  })
                  setSelectedColonia(e)
                }}
              />
            </div>
          </div>
          <div className="" style={{ maxWidth: '323px' }}>
            <Input
              className="w-full text-gray-400 opacity-70"
              placeholder="Municipio"
              readOnly
              disabled={!info.municipality}
              style={{ paddingLeft: '18px' }}
              value={info.municipality}
            />
          </div>
          <div className="" style={{ maxWidth: '323px' }}>
            <Input
              className="w-full text-gray-400 opacity-70"
              placeholder="Estado"
              readOnly
              disabled={!info.state}
              style={{ paddingLeft: '18px' }}
              value={info.state}
            />
          </div>
        </div>
      </div>

      <div className="mt-10 mb-5 flex flex-col" style={{ minWidth: '323px' }}>
        <Button className="w-full" disabled={!isInfoComplete()}>
          siguiente
        </Button>
        <p
          onClick={() => history.goBack()}
          className="text-center mt-2 underline cursor-pointer font-medium uppercase"
          style={{ fontFamily: 'Raleway', fontSize: '10px' }}
        >
          SALIR
        </p>
      </div>
    </form>
  )
}

export default StepOne
